import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import api, { formatApiError } from "@/lib/api";
import {
  Loader2, Bell, MessageSquare, MapPin, ChevronDown,
  AlertOctagon, AlertTriangle, Heart, Award, Eye, Users,
} from "lucide-react";
import { TownEyeLogo } from "@/components/TownEyeLogo";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { t } from "@/lib/strings";
import { cleanReports } from "@/lib/localityUtils";

function timeShort(iso, lang) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return lang === "ar" ? "الآن" : lang === "he" ? "עכשיו" : "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}${lang === "ar" ? "ي" : "d"}`;
  return `${Math.floor(diff / 604800)}w`;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { language, isRTL } = useI18n();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reports");
        setReports(cleanReports(data));
      } catch (e) { setError(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const cityName = useMemo(() => {
    if (!user) return "";
    if (language === "ar") return user.city_ar || user.city_en || user.city_he || "";
    if (language === "he") return user.city_he || user.city_en || user.city_ar || "";
    return user.city_en || user.city_he || user.city_ar || "";
  }, [user, language]);

  // Latest emergency-flagged report (category === "emergency")
  const emergencyReport = useMemo(
    () => reports.find((r) => (r.category || "").toLowerCase() === "emergency"),
    [reports]
  );
  const recentReports = reports.slice(0, 3);

  const topBar = (
    <div className="h-14 px-4 flex items-center justify-between gap-2">
      {/* RTL: logo will appear on right naturally due to dir attr */}
      <TownEyeLogo size={20} />

      <button
        data-testid="header-city-pill"
        className="te-pill !bg-[var(--te-primary-soft)] !text-[var(--te-primary)] px-3 py-1.5 text-[13px]"
        type="button"
      >
        <MapPin size={14} />
        <span className="truncate max-w-[120px]">{cityName || "—"}</span>
        <ChevronDown size={14} />
      </button>

      <div className="flex items-center gap-1">
        <Link
          to="/notifications"
          data-testid="header-notifications-link"
          className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
        >
          <Bell size={17} className="text-[var(--te-text)]" />
        </Link>
        <Link
          to="/messages"
          data-testid="header-messages-link"
          className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
        >
          <MessageSquare size={17} className="text-[var(--te-text)]" />
        </Link>
      </div>
    </div>
  );

  return (
    <MobileShell topBar={topBar}>
      <div className="px-4 pt-4 pb-6 space-y-5">
        {/* Emergency banner */}
        {emergencyReport && (
          <Link
            to={`/reports/${emergencyReport.id}`}
            data-testid="home-emergency-banner"
            className="block te-emergency-banner rounded-[20px] p-5 te-fade-up shadow-[0_10px_25px_-10px_rgba(177,34,34,0.6)]"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-white text-xs font-bold">
                <AlertOctagon size={14} /> {t("emergency", language)}
              </span>
            </div>
            <h3 className="text-white text-xl font-extrabold leading-tight" dir="auto">
              {emergencyReport.title}
            </h3>
            {emergencyReport.description && (
              <p className="text-white/80 text-sm mt-1.5 line-clamp-2" dir="auto">
                {emergencyReport.description}
              </p>
            )}
            <div className="flex items-center gap-1 text-white/90 text-xs font-semibold mt-3">
              <span>{isRTL ? "<" : ">"}</span>
              <span>{t("home_press_to_view", language)}</span>
            </div>
          </Link>
        )}

        {/* What's happening */}
        <div className="te-soft-section p-5 te-fade-up" data-testid="home-whats-happening">
          <h2 className="text-[20px] font-extrabold text-[var(--te-text)] leading-snug">
            {t("home_whats_happening", language, cityName || "—")}
          </h2>
          <p className="text-[var(--te-text-secondary)] text-sm mt-1">{t("home_subtitle", language)}</p>
        </div>

        {/* 4-action grid */}
        <div className="grid grid-cols-2 gap-3" data-testid="home-action-grid">
          <ActionTile
            to="/report/new"
            testId="home-action-issue"
            icon={AlertTriangle}
            iconBg="#EDE7F6"
            iconColor="#673AB7"
            title={t("home_action_issue_title", language)}
            sub={t("home_action_issue_sub", language)}
          />
          <ActionTile
            to="/report/new?priority=emergency"
            testId="home-action-emergency"
            icon={AlertOctagon}
            iconBg="#FEE2E2"
            iconColor="#DC2626"
            title={t("home_action_emergency_title", language)}
            sub={t("home_action_emergency_sub", language)}
          />
          <ActionTile
            to="/reports?category=announcement"
            testId="home-action-announcements"
            icon={Award}
            iconBg="#CFFAFE"
            iconColor="#06B6D4"
            title={t("home_action_announcements_title", language)}
            sub={t("home_action_announcements_sub", language)}
          />
          <ActionTile
            to="/community"
            testId="home-action-projects"
            icon={Heart}
            iconBg="#D1FAE5"
            iconColor="#10B981"
            title={t("home_action_projects_title", language)}
            sub={t("home_action_projects_sub", language)}
          />
        </div>

        {/* Community digest */}
        <div data-testid="home-community-digest">
          <h3 className="text-[13px] font-semibold text-[var(--te-text-secondary)] mb-2 px-1">
            {t("home_community_digest", language)}
          </h3>
          {loading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="animate-spin text-[var(--te-primary)]" />
            </div>
          ) : error ? (
            <div className="te-card p-4 text-sm text-[var(--te-danger)]">{error}</div>
          ) : recentReports.length === 0 ? (
            <div className="te-card p-6 text-center text-sm text-[var(--te-text-secondary)]">
              {language === "ar" ? "لا توجد بلاغات بعد." : language === "he" ? "אין דיווחים עדיין." : "No reports yet."}
            </div>
          ) : (
            <div className="space-y-3">
              {recentReports.map((r) => (
                <HomeReportPreview key={r.id} report={r} language={language} />
              ))}
            </div>
          )}
        </div>
      </div>
    </MobileShell>
  );
}

function ActionTile({ to, icon: Icon, iconBg, iconColor, title, sub, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="te-card te-card-hover p-4 flex flex-col justify-between min-h-[120px] te-fade-up"
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-2"
        style={{ background: iconBg }}
      >
        <Icon size={20} style={{ color: iconColor }} strokeWidth={2.2} />
      </div>
      <div>
        <h4 className="text-[15px] font-bold text-[var(--te-text)] leading-tight">{title}</h4>
        <p className="text-[12px] text-[var(--te-text-muted)] mt-0.5 leading-snug">{sub}</p>
      </div>
    </Link>
  );
}

function HomeReportPreview({ report, language }) {
  const isEmergency = (report.category || "").toLowerCase() === "emergency";
  const cityLabel = report.author?.city_ar || report.author?.city_he || report.author?.city_en;
  return (
    <Link
      to={`/reports/${report.id}`}
      data-testid={`home-report-${report.id}`}
      className={`block te-card te-card-hover te-fade-up overflow-hidden ${isEmergency ? "border-2 !border-[var(--te-danger)]" : ""}`}
    >
      {isEmergency && (
        <div className="px-4 py-1.5 bg-[var(--te-danger)] text-white text-[11px] font-bold inline-flex items-center gap-1 rounded-tr-[16px]">
          <AlertOctagon size={12} /> {t("emergency", language)}
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] flex items-center justify-center flex-shrink-0">
            <Users size={16} className="text-[var(--te-text-muted)]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-bold text-[var(--te-text)] truncate">
              {report.author?.name || "—"}
            </div>
            <div className="flex items-center gap-1.5 text-[11px] mt-0.5 flex-wrap">
              <span className="te-pill !py-0 !px-2 !text-[10px] !bg-[var(--te-primary-soft)]">{t("citizen", language)}</span>
              <span className="text-[var(--te-text-muted)]">·</span>
              <span className="text-[var(--te-text-muted)]">{timeShort(report.created_at, language)}</span>
              <span className="te-badge te-badge-open !text-[10px] !py-0">{t("report", language)}</span>
            </div>
          </div>
        </div>
        <h4 className="text-[15px] font-bold text-[var(--te-text)] mt-3 leading-tight flex items-center gap-2" dir="auto">
          {isEmergency && <AlertTriangle size={16} className="text-[var(--te-warning)]" />}
          {report.title}
        </h4>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--te-divider)] text-[12px]">
          <span className="inline-flex items-center gap-1 text-[var(--te-info)] font-semibold">
            {t("new_label", language)} <span className="w-1.5 h-1.5 rounded-full bg-[var(--te-info)]" />
          </span>
          <span className="inline-flex items-center gap-2 text-[var(--te-text-muted)]">
            <span className="inline-flex items-center gap-1">{report.comments_count || 0} <Users size={12} /></span>
            <span className="inline-flex items-center gap-1"><Eye size={12} /> {report.views_count || 8}</span>
          </span>
        </div>
      </div>
    </Link>
  );
}
